import React, { useState } from 'react';
import {
  AppBar,
  Button,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  makeStyles,
  Paper,
  Toolbar,
  Typography,
} from '@material-ui/core';
import {
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import { DoneAll, ExpandLess, ExpandMore } from '@material-ui/icons';
import { useNavigate } from 'react-router-dom';
import { useConnectedWallets } from '../utils/connected-wallets';
import { useIsExtensionWidth } from '../utils/utils';
import { useWalletSelector } from '../utils/wallet';
import { verifyPassword, walletHasPassword } from '../utils/wallet-seed';
import { isAutoApproveActive, autoApproveFields } from '../utils/auto-approve';
import DialogForm from './DialogForm';
import strings from '../localization';
import { chromeLocalStorage } from '../utils/chrome-storage-polyfill';

export default function ConnectionsList() {
  const isExtensionWidth = useIsExtensionWidth();
  const connectedWallets = useConnectedWallets();

  return (
    <Paper>
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar>
          <Typography
            variant="h6"
            style={{ flexGrow: 1, fontSize: isExtensionWidth && '1rem' }}
            component="h2"
          >
            {strings.connectedDapps}
          </Typography>
        </Toolbar>
      </AppBar>
      <List disablePadding>
        {Object.entries(connectedWallets).map(([origin, connectedWallet]) => (
          <ConnectionsListItem
            origin={origin}
            connectedWallet={connectedWallet}
            key={origin}
          />
        ))}
      </List>
    </Paper>
  );
}

const ICON_SIZE = 28;
const IMAGE_SIZE = 24;

const useStyles = makeStyles((theme) => ({
  itemDetails: {
    marginLeft: theme.spacing(3),
    marginRight: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'space-evenly',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  listItemIcon: {
    backgroundColor: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: ICON_SIZE,
    width: ICON_SIZE,
    borderRadius: ICON_SIZE / 2,
  },
  listItemImage: {
    height: IMAGE_SIZE,
    width: IMAGE_SIZE,
    borderRadius: IMAGE_SIZE / 2,
  },
}));

function ConnectionsListItem({ origin, connectedWallet }) {
  const classes = useStyles();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  // TODO better way to get high res icon?
  const appleIconUrl = origin + '/apple-touch-icon.png';
  const faviconUrl = origin + '/favicon.ico';
  const [iconUrl, setIconUrl] = useState(appleIconUrl);
  const { accounts } = useWalletSelector();
  // TODO better way to do this
  const account = accounts.find(
    (account) => account.address.toBase58() === connectedWallet.publicKey,
  );

  const [authOpen, setAuthOpen] = useState(false);

  const autoApproveActive = isAutoApproveActive(connectedWallet);

  const setAutoApprove = (enabled) => {
    chromeLocalStorage.get('connectedWallets', (result) => {
      result.connectedWallets[origin] = {
        ...result.connectedWallets[origin],
        ...autoApproveFields(enabled),
      };
      chromeLocalStorage.set({ connectedWallets: result.connectedWallets });
    });
  };

  // Enabling auto-approve is sensitive; require the password. Disabling is free.
  const toggleAutoApprove = () => {
    if (autoApproveActive) {
      setAutoApprove(false);
    } else {
      setAuthOpen(true);
    }
  };

  const disconnectWallet = () => {
    chromeLocalStorage.get('connectedWallets', (result) => {
      delete result.connectedWallets[origin];
      chromeLocalStorage.set({ connectedWallets: result.connectedWallets });
    });
  };

  return (
    <>
      {/* Row click re-enters the dapp; the chevron toggles connection options. */}
      <ListItem
        button
        onClick={() => navigate('/browser', { state: { url: origin } })}
      >
        <ListItemIcon>
          <div className={classes.listItemIcon}>
            <img
              src={iconUrl}
              onError={() => setIconUrl(faviconUrl)}
              className={classes.listItemImage}
              alt=""
            />
          </div>
        </ListItemIcon>
        <div style={{ display: 'flex', flex: 1 }}>
          <ListItemText primary={origin} secondary={account?.name} />
        </div>
        <IconButton
          edge="end"
          size="small"
          aria-label={open ? 'Hide options' : 'Show options'}
          onClick={(event) => {
            event.stopPropagation();
            setOpen((open) => !open);
          }}
        >
          {open ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </ListItem>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <div className={classes.itemDetails}>
          <div className={classes.buttonContainer}>
            <Button
              variant={autoApproveActive ? 'contained' : 'outlined'}
              color="primary"
              size="small"
              startIcon={<DoneAll />}
              onClick={toggleAutoApprove}
            >
              {autoApproveActive ? 'Auto-Approved' : 'Auto-Approve'}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={disconnectWallet}
            >
              {strings.disconnect}
            </Button>
          </div>
        </div>
      </Collapse>
      <AutoApproveAuthDialog
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        onVerified={() => {
          setAuthOpen(false);
          setAutoApprove(true);
        }}
      />
    </>
  );
}

function AutoApproveAuthDialog({ open, onClose, onVerified }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState(false);
  const [checking, setChecking] = useState(false);
  // No password on file: nothing to verify, so the prompt is a plain confirm.
  const passwordless = !walletHasPassword();

  const submit = async () => {
    if (passwordless) {
      onVerified();
      return;
    }
    setChecking(true);
    const ok = await verifyPassword(password);
    setChecking(false);
    if (ok) {
      setPassword('');
      onVerified();
    } else {
      setError(true);
    }
  };

  return (
    <DialogForm open={open} onClose={onClose} fullWidth>
      <DialogTitle>{strings.enableAutoApproveTitle}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {strings.enableAutoApproveWarning}
        </DialogContentText>
        <TextField
          style={{ display: passwordless ? 'none' : undefined }}
          autoFocus
          fullWidth
          margin="normal"
          variant="outlined"
          label={strings.password}
          type="password"
          autoComplete="current-password"
          value={password}
          error={error}
          helperText={error ? strings.incorrectPassword : ''}
          onChange={(e) => {
            setPassword(e.target.value);
            setError(false);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submit();
            }
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{strings.cancel}</Button>
        <Button
          color="primary"
          onClick={submit}
          disabled={checking || (!passwordless && !password)}
        >
          {passwordless ? strings.continue : strings.unlock}
        </Button>
      </DialogActions>
    </DialogForm>
  );
}
